"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
} from "@/components/ui/modal";
import { cn } from "@/lib/utils";
import { forexSymbols } from "@/services/forex/forex-symbols";
import { PropsWithClassName } from "@/types";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React from "react";

type Currency = (typeof forexSymbols)[keyof typeof forexSymbols];

const defaultCurrency = forexSymbols["USD"];
const currencies = Object.values(forexSymbols).map((symbol) => symbol);

interface CurrencySelectorProps {
  readonly code?: string;
  readonly onChange?: (code: string) => void;
}
export default function CurrencySelector(
  props: PropsWithClassName<CurrencySelectorProps>,
) {
  const t = useTranslations("Currency");
  const [open, setOpen] = React.useState(false);
  const [selectedCurrency, setSelectedCurrency] = React.useState<Currency>(
    props.code
      ? forexSymbols[props.code as keyof typeof forexSymbols] || defaultCurrency
      : defaultCurrency,
  );

  const handleCurrencyChange = (currency: Currency) => {
    setSelectedCurrency(currency);
    if (props.onChange) {
      props.onChange(currency.code);
    }
    setOpen(false);
  };

  return (
    <Modal isRoute={false} open={open} onOpenChange={setOpen}>
      <ModalTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-64 items-center justify-between",
            props.className,
          )}
        >
          {selectedCurrency ? (
            <Image
              src={selectedCurrency.image}
              alt={selectedCurrency.name}
              width={48}
              height={32}
              className="size-6 rounded-full border object-cover"
            />
          ) : null}
          {selectedCurrency
            ? `${selectedCurrency.code} (${selectedCurrency.symbol})`
            : t("select")}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </ModalTrigger>
      <ModalContent className="w-[400px] bg-popover p-0">
        <ModalHeader>
          <ModalTitle className="px-4 pt-4">{t("search")}</ModalTitle>
        </ModalHeader>
        <Command className="rounded-none border-t">
          <CommandInput placeholder={t("search")} />
          <CommandList>
            <CommandEmpty>{t("notFound")}</CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-auto">
              {currencies.map((currency) => (
                <CommandItem
                  key={currency.code}
                  onSelect={() => handleCurrencyChange(currency)}
                  aria-selected={selectedCurrency.code === currency.code}
                  className="hover:cursor-pointer"
                  role="option"
                >
                  <div className="flex grow items-center gap-2">
                    <Image
                      src={currency.image}
                      alt={currency.name}
                      width={48}
                      height={32}
                      className="size-6 rounded-full border object-cover"
                    />
                    <div>
                      <p className="font-semibold text-foreground">
                        {currency.code}
                        <span className="ml-2 font-bold text-muted-foreground">
                          {currency.symbol}
                        </span>
                      </p>
                      <p className="text-muted-foreground">{currency.name}</p>
                    </div>
                  </div>
                  <CheckIcon
                    className={cn(
                      "size-4",
                      selectedCurrency.code === currency.code
                        ? "stroke-[3] font-semibold text-green-500 opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </ModalContent>
    </Modal>
  );
}
